import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { UserManagementPage } from './pages/UserManagementPage';
import { DocumentsPage } from './pages/DocumentsPage';
import AssetsPage from './pages/AssetsPage';
import TrainingsPage from './pages/TrainingsPage';
import CodelistsPage from './pages/CodelistsPage';
import { DebugPage } from './pages/DebugPage';
import { Layout } from './components/layout/Layout';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { ResetPasswordForm } from './components/auth/ResetPasswordForm';
import { AcceptInvitationForm } from './components/auth/AcceptInvitationForm';
import { EmailVerificationForm } from './components/auth/EmailVerificationForm';
import { Toaster } from './components/ui/toaster';
import { useAuthStore } from './store/authStore';

function App() {
  const { checkAuth, isAuthenticated } = useAuthStore();

  useEffect(() => {
    // Check authentication status on app load
    checkAuth();
  }, [checkAuth]);

  return (
    <Router>
      <div className="App">
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/reset-password" element={<ResetPasswordForm />} />
          <Route path="/accept-invitation" element={<AcceptInvitationForm />} />
          <Route path="/verify-email/:token" element={<EmailVerificationForm />} />
          {process.env.NODE_ENV === 'development' && (
            <Route path="/debug" element={<DebugPage />} />
          )}
          
          {/* Protected routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />
            
            {/* Placeholder routes for future sprint implementation */}
            <Route path="compliance" element={<div className="p-6"><h1 className="text-2xl font-bold">Compliance Module</h1><p className="text-gray-600 mt-2">Coming in Sprint 5 - NIS2 compliance tracking and scoring</p></div>} />
            <Route path="documents" element={<DocumentsPage />} />
            <Route path="assets" element={<AssetsPage />} />
            <Route path="training" element={<TrainingsPage />} />
            <Route path="codelists" element={<CodelistsPage />} />
            <Route path="incidents" element={<div className="p-6"><h1 className="text-2xl font-bold">Incident Management</h1><p className="text-gray-600 mt-2">Coming in Sprint 4 - Cybersecurity incident tracking and response</p></div>} />
            <Route path="reports" element={<div className="p-6"><h1 className="text-2xl font-bold">Reports & Analytics</h1><p className="text-gray-600 mt-2">Coming in Sprint 5 - Compliance reports and analytics</p></div>} />
            <Route path="files" element={<div className="p-6"><h1 className="text-2xl font-bold">File Manager</h1><p className="text-gray-600 mt-2">Coming in Sprint 2 - Document file management</p></div>} />
            <Route 
              path="users" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <UserManagementPage />
                </ProtectedRoute>
              } 
            />
            <Route path="settings" element={<div className="p-6"><h1 className="text-2xl font-bold">Settings</h1><p className="text-gray-600 mt-2">User settings and preferences</p></div>} />
          </Route>
          
          {/* Email verification placeholder */}
          <Route 
            path="/verify-email" 
            element={
              <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                  <h1 className="text-2xl font-bold mb-4">Email Verification Required</h1>
                  <p className="text-gray-600">Please check your email and click the verification link to activate your account.</p>
                </div>
              </div>
            } 
          />
          
          {/* Catch all route */}
          <Route 
            path="*" 
            element={
              isAuthenticated ? 
                <Navigate to="/dashboard" replace /> : 
                <Navigate to="/login" replace />
            } 
          />
        </Routes>
        
        {/* Toast notifications */}
        <Toaster />
      </div>
    </Router>
  );
}

export default App;
